/**
 * Whether a job's options are things to choose between or things to buy.
 *
 * `jobs.multi_option` is one checkbox with two very different meanings. Off,
 * the estimates on a job are rival quotes for one piece of work and the
 * customer picks one. On, they are separate pieces of work and they add up
 * (ADR 0030, and ADR 0120 for the customer's side of it).
 *
 * Until 2026-09-09 it started off and a rep had to remember to tick it, which
 * is the wrong way round. Jonathon:
 *
 * > let's default to when there's multiple estimates to where they can
 * > purchase any of them. unless there's only full roof replacements offered
 * > then it should default to being toggled off because a customer wouldn't
 * > buy multiple roof systems for the same house
 *
 * So: **a roof and the gutters are both for sale; two roofs are a choice.**
 *
 * This is a default, not a rule. The moment a rep changes the checkbox their
 * answer is the answer and nothing below recomputes it — `jobs`
 * `.multi_option_manual` is what remembers that they did.
 */

/** An estimate, as this decision needs to see one. */
export interface MultiOptionEstimate {
  /** `estimates.trade` — "Roofing Replacement", "Gutters", "Roofing Repairs". */
  trade?: string | null;
}

/**
 * Whether a trade says nothing at all — empty, or only whitespace.
 *
 * `trim()`, so a tab, a newline or a no-break space pasted into the box is as
 * blank as nothing. The database's `trade_is_blank` strips exactly the same
 * characters, and supabase/test/multi-option-default.test.ts holds the two
 * together: until 2026-09-10 the SQL side used `btrim`, which strips only
 * spaces, and a lone tab was typed to one and blank to the other.
 */
export function tradeIsBlank(trade: string | null | undefined): boolean {
  return (trade ?? "").trim() === "";
}

/**
 * Whether this estimate is a whole roof going on the house.
 *
 * Deliberately looser than `trade === "Roofing Replacement"`. Trades are free
 * text and new ones are added rather than renamed (CLAUDE.md rule 8), so
 * "Roof Replacement" or "Metal Roof Replacement" have to land here too — they
 * are the same roof, sold under a name somebody typed later.
 *
 * A repair is not one of these however it is named, which is the same test
 * `isReplacementTrade` in contract.ts makes; nor are the gutters, the siding
 * or a deck, which are all things a customer can buy *alongside* a roof.
 *
 * An estimate with no trade on it is not evidence of a roof, and it is not
 * evidence of anything else either — see `multiOptionDefault`, which waits for
 * it rather than guessing.
 */
export function isFullRoofReplacement(
  trade: string | null | undefined,
): boolean {
  const name = (trade ?? "").trim().toLowerCase();
  if (name === "") return false;
  if (name.includes("repair")) return false;
  return name.includes("roof");
}

/**
 * An option as whole-roof-ness needs to see it: its own trade, and the trades
 * on its line items for when it has none.
 */
export interface RoofOption {
  /** `estimates.trade`. */
  trade?: string | null | undefined;
  /** `estimate_items.trade` for each of its lines. Absent when not loaded. */
  itemTrades?: readonly (string | null | undefined)[] | undefined;
}

/**
 * Whether this option, as offered on a document, is a whole roof.
 *
 * Its own trade when it has one — the rep typed "Gutters", and a stray roofing
 * line does not turn the gutters into a roof. When the trade is blank, its
 * line items say: an estimate started from scratch whose lines are "Roofing
 * Replacement" is a roof option. The same fallback the Present screen already
 * reads for the agreement (`contractKindFor` over the estimate's trade and its
 * lines' trades); #195 is the case it keeps.
 *
 * One answer for three things, so they cannot disagree: the `wholeRoof` frozen
 * onto a package, whether two roofs are rivals when the document is built, and
 * which packages carry the Key Differentiator (ADR 0124).
 *
 * Not `multiOptionDefault`, which the database mirrors from trades alone and
 * which waits for a blank trade rather than reading lines.
 */
export function isWholeRoofOption(option: RoofOption): boolean {
  if (!tradeIsBlank(option.trade)) return isFullRoofReplacement(option.trade);
  return (option.itemTrades ?? []).some((trade) =>
    isFullRoofReplacement(trade),
  );
}

/**
 * Whether this job's estimates should start out as things to buy, or `null`
 * to leave the box as it stands.
 *
 * Three conditions, and the third is the quiet one:
 *
 *  - **More than one estimate.** One is not a choice of any kind.
 *  - **Not all of them roofs.** Two roof replacements are alternatives; nobody
 *    puts two roof systems on one house. A roof and the gutters are not.
 *  - **Every one of them says what it is.** An estimate with no trade has not
 *    said whether it is another roof or something to buy alongside one, so
 *    there is no answer yet — `null`, and the box keeps whatever it said
 *    before. Not `false`: that would switch off a roof-and-gutters job the
 *    moment a third estimate was started, and switch it back on when its trade
 *    was typed. The moment the trade is filled in, the answer follows.
 *
 * Turning the box on does not by itself make every option active — the
 * cheapest-option rule still picks one unless the others are pinned or signed
 * (ADR 0030). What it changes is that a customer may buy several, and that
 * every one they sign stays active and adds up.
 */
export function multiOptionDefault(
  estimates: readonly MultiOptionEstimate[],
): boolean | null {
  if (estimates.length < 2) return false;
  if (estimates.some((estimate) => tradeIsBlank(estimate.trade))) return null;
  return !estimates.every((estimate) => isFullRoofReplacement(estimate.trade));
}

/**
 * Whether a customer may buy more than one whole roof on this job's document.
 *
 * Only when a person ticked "Customer can buy more than one of these"
 * themselves. Jonathon, 2026-09-10, on a house and a detached garage:
 *
 * > I don't want a house and detached garage as two documents. I just don't
 * > want it to default to being able to purchase more than one roof. It can be
 * > selected though to make things simpler.
 *
 * So the default is unchanged — a job of roofs defaults off, and a roof and the
 * gutters default on with the roofs still rivals — and the box set by hand
 * (`multi_option_manual`) is the rep saying these roofs are for different
 * buildings. See ADR 0124.
 */
export function roofsBuyableTogether(
  multiOption: boolean | null | undefined,
  multiOptionManual: boolean | null | undefined,
): boolean {
  return multiOption === true && multiOptionManual === true;
}

/**
 * Whether any two of these could be bought together.
 *
 * Every whole roof is a rival of every other — a customer buys at most one of
 * them — so a list of three roofs is still one choice, and a list of two roofs
 * and the gutters is a choice of roof plus the gutters. See `toggleChoice` in
 * presentation.ts, which is where that is enforced on the customer's page.
 *
 * Unless `roofsTogether` (`roofsBuyableTogether`): then nothing on the list is
 * a rival of anything else, and any two will do.
 */
export function canBuyTogether(
  trades: readonly (string | null | undefined)[],
  roofsTogether = false,
): boolean {
  return anyTwoTogether(
    trades.map((trade) => isFullRoofReplacement(trade)),
    roofsTogether,
  );
}

/**
 * `canBuyTogether`, asked of whether each option is a whole roof rather than
 * of its trade — for a caller that has worked that out with more to go on
 * (`isWholeRoofOption`).
 */
export function anyTwoTogether(
  wholeRoofs: readonly boolean[],
  roofsTogether = false,
): boolean {
  if (roofsTogether) return wholeRoofs.length >= 2;
  const roofs = wholeRoofs.filter(Boolean).length;
  const others = wholeRoofs.length - roofs;
  return others + Math.min(roofs, 1) >= 2;
}

/**
 * What the estimate card says under "Customer can buy more than one of these".
 *
 * The box has two states that mean different things (ADR 0124): set for you,
 * two roofs are still a choice; set by you and on, two roofs can be bought
 * together. A rep who unticks and re-ticks a box that came on by default has
 * moved from the first to the second with nothing on the box to show it, so
 * the sentence under it has to — and the default's sentence says what setting
 * it by hand would do.
 */
export function multiOptionHint(
  multiOption: boolean | null | undefined,
  multiOptionManual: boolean | null | undefined,
): string {
  if (roofsBuyableTogether(multiOption, multiOptionManual)) {
    return (
      "You set this, so the customer can buy any of them together, two roofs " +
      "included — right for a house and a garage, wrong for two alternatives " +
      "for the same roof. It stays as you left it."
    );
  }
  if (multiOptionManual === true)
    return "You set this. It stays as you left it.";
  return (
    "Set for you: options that are different work are for sale together; two " +
    "roofs are a choice. Ticking it yourself lets them buy two roofs together."
  );
}
